import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { SimManager } from "@learningblocks/simulation-engine";

import "./styles.scss";
import ValueDisplay from "./components/ValueDisplay/ValueDisplay";
import PlateViewer from "./components/PlateViewer/PlateViewer";
import Timer from "./components/Timer/Timer";
import Toggle from "./components/Toggle/Toggle";
import { setWavelength, setTemperature, setIsShakeOn } from "../../store";

export type WellPosition = `${string}${number}`;
interface TimerInterface {
  hours: number;
  minutes: number;
  seconds: number;
}

interface ContentProps {
  wavelength: number;
  temperature: number;
  isShakeOn: boolean;
  wellsContent: Record<WellPosition, { [key: string]: number }>;
  timer: TimerInterface;
}

export const Content = (props: ContentProps) => {
  const dispatch = useDispatch();
  const incrementWavelength = () => {
    dispatch(
      setWavelength(
        props.wavelength < 1000 ? props.wavelength + 1 : props.wavelength
      )
    );
  };
  const wellsType = useSelector(
    (state: any) => state.microplateReader.wellsType
  );

  const decrementWavelength = () => {
    dispatch(
      setWavelength(
        props.wavelength > 230 ? props.wavelength - 1 : props.wavelength
      )
    );
  };

  const incrementTemperature = () => {
    dispatch(
      setTemperature(
        props.temperature < 45 ? props.temperature + 1 : props.temperature
      )
    );
  };

  const decrementTemperature = () => {
    dispatch(
      setTemperature(
        props.temperature > 4 ? props.temperature - 1 : props.temperature
      )
    );
  };

  const toggleShake = () => {
    dispatch(setIsShakeOn(!props.isShakeOn));
  };

  const handleReaderErrors = () => {
    if (props.wavelength !== 470) {
      SimManager.getInstance().addToast(
        "Warning!",
        "<b>Oops!</b> Your wavelength is not set correctly. Adjust the settings and try again.",
        "Warning"
      );
      return true;
    }

    const blankWellsError = !(
      wellsType["A1"] === "blank" &&
      wellsType["A2"] === "blank" &&
      wellsType["A3"] === "blank"
    );
    if (blankWellsError) {
      SimManager.getInstance().addToast(
        "Warning!",
        "<b>Oops!</b> You did not select the blank correctly. The blank wells are the negative control. Which wells contain no enzyme and are expected to have no reaction?",
        "Warning"
      );
      return true;
    }

    let sampleWellsError = false;
    const rows = ["A", "B", "C", "D", "E", "F", "G"];
    for (const row of rows) {
      for (let col = 1; col <= 3; col++) {
        const well = `${row}${col}`;
        if (wellsType[well] !== "blank" && wellsType[well] !== "sample") {
          sampleWellsError = true;
          break;
        }
      }
    }

    if (sampleWellsError) {
      SimManager.getInstance().addToast(
        "Warning!",
        "<b>Oops!</b> You did not select the sample wells correctly. The sample wells are any well that has been filled with liquid. Select all the wells that contain liquid. Do not select empty wells.",
        "Warning"
      );
      return true;
    }

    if (!props.isShakeOn) {
      SimManager.getInstance().addToast(
        "Warning!",
        "<b>Oops!</b> You need to shake the plate to mix the reaction. Adjust the settings and try again.",
        "Warning"
      );
      return true;
    }

    if (props.temperature !== 20) {
      SimManager.getInstance().addToast(
        "Warning!",
        "<b>Oops!</b> This enzyme functions best at a specific temperature. Adjust the settings and try again.",
        "Warning"
      );
      return true;
    }

    if (
      !(
        props.timer.hours === 0 &&
        props.timer.minutes === 5 &&
        props.timer.seconds === 0
      )
    ) {
      SimManager.getInstance().addToast(
        "Warning!",
        "<b>Oops!</b> The timer is not set correctly. Adjust the settings and try again.",
        "Warning"
      );
      return true;
    }

    return false;
  };

  return (
    <div className="instrument-interface">
      <div className="left-panel">
        <ValueDisplay
          title="Wavelength"
          value={`${props.wavelength}`}
          unit="nm"
          onIncrement={incrementWavelength}
          onDecrement={decrementWavelength}
        />

        <ValueDisplay
          title="Temperature"
          value={`${props.temperature}°C`}
          unit="°C"
          onIncrement={incrementTemperature}
          onDecrement={decrementTemperature}
        />
      </div>

      <div className="center-panel">
        <PlateViewer wellsContent={props.wellsContent} title="Plate viewer" />
      </div>

      <div className="right-panel">
        <div className="shake-control">
          <Toggle label="Shake" isOn={props.isShakeOn} onToggle={toggleShake} />
        </div>

        <Timer
          title="Timer"
          timer={props.timer}
          handleReaderErrors={handleReaderErrors}
        />
      </div>
    </div>
  );
};
